import React, { useMemo, useState } from 'react';
import ArcGraphCoachView from '../../components/coach/arc/ArcGraphCoachView';
import { SAMPLE_ARC_STROKES } from '../../features/dashboards/mocks/rowingArcSamples';
import { useArcSessionStrokeSamples } from '../../hooks/useArcMetrics';
import type { StrokeSample } from '../../features/dashboards/rowingArcTypes';
import { ARC_PHYSICS } from '../../features/dashboards/arcConfig';

interface ArcPrototypePageProps {
  sessionId: string | null;
}

const deriveStrokeDurationMs = (strokes: StrokeSample[]): number => {
  const durations: number[] = [];
  for (const s of strokes) {
    if (s.driveTimeMs != null && s.recoveryTimeMs != null) {
      durations.push(s.driveTimeMs + s.recoveryTimeMs);
    }
  }
  if (durations.length > 0) {
    return durations.reduce((a, b) => a + b, 0) / durations.length;
  }
  return ARC_PHYSICS.STROKE_DURATION_MS;
};

const ArcPrototypePage: React.FC<ArcPrototypePageProps> = ({ sessionId }) => {
  /** Session = telemetry for picker; Sample = built-in demo strokes. */
  const [useSampleData, setUseSampleData] = useState(false);

  const sessionQuery = useArcSessionStrokeSamples(sessionId, {
    enabled: !useSampleData,
  });

  const strokes = useMemo((): StrokeSample[] => {
    if (useSampleData) {
      return SAMPLE_ARC_STROKES;
    }
    if (sessionId && sessionQuery.data && sessionQuery.data.length > 0) {
      return sessionQuery.data;
    }
    if (!sessionId) {
      return SAMPLE_ARC_STROKES;
    }
    return [];
  }, [useSampleData, sessionId, sessionQuery.data]);

  const availableSeats = useMemo(() => {
    const seats = new Set<number>();
    strokes.forEach((stroke) => {
      if (stroke.rowerPosition != null) {
        seats.add(stroke.rowerPosition);
      }
    });
    return Array.from(seats).sort((a, b) => a - b);
  }, [strokes]);

  const initialSeat = availableSeats[0] ?? 5;

  const strokeDurationMs = useMemo(() => deriveStrokeDurationMs(strokes), [strokes]);

  const coachKey = `${useSampleData ? 'sample' : (sessionId ?? 'offline')}`;

  if (!useSampleData && sessionId && sessionQuery.isLoading) {
    return (
      <>
        <ArcDataSourceToggle useSampleData={useSampleData} onChange={setUseSampleData} />
        <div className="flex min-h-[400px] items-center justify-center bg-rowing-gray-50 px-3 py-12 sm:px-4">
          <p className="text-sm text-rowing-gray-600">Loading session arc data…</p>
        </div>
      </>
    );
  }

  if (!useSampleData && sessionId && sessionQuery.isError) {
    return (
      <>
        <ArcDataSourceToggle useSampleData={useSampleData} onChange={setUseSampleData} />
        <div className="flex min-h-[400px] flex-col items-center justify-center gap-2 bg-rowing-gray-50 px-3 py-12 sm:px-4">
          <p className="text-sm font-medium text-rowing-gray-900">Could not load arc stroke data</p>
          <p className="text-xs text-rowing-gray-600 text-center max-w-md">
            {(sessionQuery.error as Error)?.message ?? 'Unknown error'}
          </p>
        </div>
      </>
    );
  }

  if (!useSampleData && sessionId && !sessionQuery.isLoading && strokes.length === 0) {
    return (
      <>
        <ArcDataSourceToggle useSampleData={useSampleData} onChange={setUseSampleData} />
        <div className="flex min-h-[400px] items-center justify-center bg-rowing-gray-50 px-3 py-12 sm:px-4">
          <p className="max-w-md text-center text-sm text-rowing-gray-600">
            No per-stroke arc samples for this session. The session may lack periodic gate
            angle/force data, or strokes could not be segmented.
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <ArcDataSourceToggle useSampleData={useSampleData} onChange={setUseSampleData} />
      <ArcGraphCoachView
        key={coachKey}
        sessionId={useSampleData ? null : sessionId}
        strokes={strokes}
        availableSeats={availableSeats.length > 0 ? availableSeats : [1, 2, 3, 4, 5, 6, 7, 8]}
        initialSeat={initialSeat}
        strokeDurationMs={strokeDurationMs}
      />
    </>
  );
};

const ArcDataSourceToggle: React.FC<{
  useSampleData: boolean;
  onChange: (next: boolean) => void;
}> = ({ useSampleData, onChange }) => (
  <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50/80 px-3 py-2 text-xs text-amber-950">
    <span className="font-medium">Arc prototype data</span>
    <div className="inline-flex rounded-md border border-amber-300/80 bg-white p-0.5 shadow-sm">
      <button
        type="button"
        onClick={() => onChange(false)}
        className={`rounded px-2.5 py-1 font-medium transition-colors ${
          !useSampleData
            ? 'bg-accent text-accent-foreground'
            : 'text-amber-900 hover:bg-amber-100/80'
        }`}
      >
        Session
      </button>
      <button
        type="button"
        onClick={() => onChange(true)}
        className={`rounded px-2.5 py-1 font-medium transition-colors ${
          useSampleData
            ? 'bg-accent text-accent-foreground'
            : 'text-amber-900 hover:bg-amber-100/80'
        }`}
      >
        Sample
      </button>
    </div>
    <span className="text-amber-800/90">
      {useSampleData
        ? 'Built-in demo strokes (session picker ignored).'
        : 'Telemetry for the selected session.'}
    </span>
  </div>
);

export default ArcPrototypePage;
